/**
 * Generated by @openapi-codegen
 *
 * @version 1.0.0
 */
import * as reactQuery from 'app/src/shared/helpers/reactquery'
import { ShoppingContext, useShoppingContext } from './shopping-context'
import type * as Fetcher from './shopping-fetcher'
import { shoppingFetch } from './shopping-fetcher'
import type * as Schemas from './shopping-schemas'

export type ActivateError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
  | {
      status: 409
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
>

export type ActivateVariables = {
  body?: Schemas.ActivateRequest
} & ShoppingContext['fetcherOptions']

/**
 * Start activation process
 */
export const fetchActivate = (variables: ActivateVariables, signal?: AbortSignal) =>
  shoppingFetch<Schemas.ActivateResult, ActivateError, Schemas.ActivateRequest, {}, {}, {}>({
    url: '/activate',
    method: 'post',
    ...variables,
    signal,
  })

/**
 * Start activation process
 */
export const useActivate = (
  options?: Omit<reactQuery.UseMutationOptions<Schemas.ActivateResult, ActivateError, ActivateVariables>, 'mutationFn'>,
) => {
  const { fetcherOptions } = useShoppingContext()
  return reactQuery.useMutation<Schemas.ActivateResult, ActivateError, ActivateVariables>({
    mutationFn: (variables: ActivateVariables) => fetchActivate({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type MultiActivateError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
  | {
      status: 409
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
>

export type MultiActivateVariables = {
  body?: Schemas.MultiActivateRequest
} & ShoppingContext['fetcherOptions']

/**
 * Start activation process
 */
export const fetchMultiActivate = (variables: MultiActivateVariables, signal?: AbortSignal) =>
  shoppingFetch<Schemas.ActivateResult, MultiActivateError, Schemas.MultiActivateRequest, {}, {}, {}>({
    url: '/activate/multi',
    method: 'post',
    ...variables,
    signal,
  })

/**
 * Start activation process
 */
export const useMultiActivate = (
  options?: Omit<reactQuery.UseMutationOptions<Schemas.ActivateResult, MultiActivateError, MultiActivateVariables>, 'mutationFn'>,
) => {
  const { fetcherOptions } = useShoppingContext()
  return reactQuery.useMutation<Schemas.ActivateResult, MultiActivateError, MultiActivateVariables>({
    mutationFn: (variables: MultiActivateVariables) => fetchMultiActivate({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type OrderListQueryParams = {
  /**
   * Position of the first element to return
   *
   * @format int32
   * @default 0
   * @minimum 0
   */
  pagingOffset?: number
  /**
   * Maximum number of rows to return
   *
   * @format int32
   * @default 20
   * @minimum 1
   */
  pagingLimit?: number
  /**
   * the status to filter the orders by.
   */
  status?: ('confirmed' | 'rejected' | 'used' | 'submitted' | 'refused' | 'approved' | 'canceled')[]
  /**
   * the typology to filter the orders by.
   */
  typology?: string
  /**
   * include or exclude the orders with additional info prize.
   */
  additionalInfoPrize?: boolean
  /**
   * include or exclude the orders with additional info virtualMoney.
   */
  additionalInfoVirtualMoney?: boolean
  /**
   * the parent order id to filter the orders by.
   */
  parentOrderId?: string
  /**
   * The membership ID used to filter orders.
   */
  membershipId?: string
  /**
   * The start date of the orders to be listed, format yyyy-MM-dd.
   *
   * @format date-time
   */
  from?: string
  /**
   * The end date of the orders to be listed, format yyyy-MM-dd.
   *
   * @format date-time
   */
  to?: string
  /**
   * The sort order, +date (ascending) or -date (descending).
   *
   * @default -date
   */
  sort?: '+date' | '-date'
  /**
   * include or exclude the user info in the response.
   */
  includeUserInfo?: boolean
  /**
   * include or exclude the purchase details in the response.
   */
  includePurchaseDetails?: boolean
}

export type OrderListError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
  | {
      status: 404
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
>

export type OrderListVariables = {
  queryParams?: OrderListQueryParams
} & ShoppingContext['fetcherOptions']

/**
 * Returns the list of the orders
 */
export const fetchOrderList = (variables: OrderListVariables, signal?: AbortSignal) =>
  shoppingFetch<Schemas.OrderPaginated, OrderListError, undefined, {}, OrderListQueryParams, {}>({
    url: '/orders',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * Returns the list of the orders
 */
export const useOrderList = <TData = Schemas.OrderPaginated>(
  variables: OrderListVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.OrderPaginated, OrderListError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useShoppingContext(options)
  return reactQuery.useQuery<Schemas.OrderPaginated, OrderListError, TData>({
    queryKey: queryKeyFn({
      path: '/orders',
      operationId: 'orderList',
      variables,
    }),
    queryFn: ({ signal }) => fetchOrderList({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type OrderGetPathParams = {
  /**
   * ID of the order to process
   */
  orderId: string
}

export type OrderGetError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
  | {
      status: 404
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
>

export type OrderGetVariables = {
  pathParams: OrderGetPathParams
} & ShoppingContext['fetcherOptions']

/**
 * get an order
 */
export const fetchOrderGet = (variables: OrderGetVariables, signal?: AbortSignal) =>
  shoppingFetch<Schemas.OrderDetails, OrderGetError, undefined, {}, {}, OrderGetPathParams>({
    url: '/orders/{orderId}',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * get an order
 */
export const useOrderGet = <TData = Schemas.OrderDetails>(
  variables: OrderGetVariables,
  options?: Omit<reactQuery.UseQueryOptions<Schemas.OrderDetails, OrderGetError, TData>, 'queryKey' | 'queryFn' | 'initialData'>,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useShoppingContext(options)
  return reactQuery.useQuery<Schemas.OrderDetails, OrderGetError, TData>({
    queryKey: queryKeyFn({
      path: '/orders/{orderId}',
      operationId: 'orderGet',
      variables,
    }),
    queryFn: ({ signal }) => fetchOrderGet({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type OrderStartPathParams = {
  /**
   * ID of the order to process
   */
  orderId: string
}

export type OrderStartError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
  | {
      status: 404
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
  | {
      status: 409
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
>

export type OrderStartVariables = {
  pathParams: OrderStartPathParams
} & ShoppingContext['fetcherOptions']

/**
 * start draft order processing
 */
export const fetchOrderStart = (variables: OrderStartVariables, signal?: AbortSignal) =>
  shoppingFetch<undefined, OrderStartError, undefined, {}, {}, OrderStartPathParams>({
    url: '/orders/{orderId}/start',
    method: 'post',
    ...variables,
    signal,
  })

/**
 * start draft order processing
 */
export const useOrderStart = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, OrderStartError, OrderStartVariables>, 'mutationFn'>,
) => {
  const { fetcherOptions } = useShoppingContext()
  return reactQuery.useMutation<undefined, OrderStartError, OrderStartVariables>({
    mutationFn: (variables: OrderStartVariables) => fetchOrderStart({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type OrderAttachmentGetPathParams = {
  /**
   * ID of the order to process
   */
  orderId: string
  /**
   * ID of the attachment to process
   */
  attachmentId: string
}

export type OrderAttachmentGetError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
  | {
      status: 404
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
>

export type OrderAttachmentGetVariables = {
  pathParams: OrderAttachmentGetPathParams
} & ShoppingContext['fetcherOptions']

/**
 * get an attachment
 */
export const fetchOrderAttachmentGet = (variables: OrderAttachmentGetVariables, signal?: AbortSignal) =>
  shoppingFetch<undefined, OrderAttachmentGetError, undefined, {}, {}, OrderAttachmentGetPathParams>({
    url: '/orders/{orderId}/attachments/{attachmentId}',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * get an attachment
 */
export const useOrderAttachmentGet = <TData = undefined>(
  variables: OrderAttachmentGetVariables,
  options?: Omit<reactQuery.UseQueryOptions<undefined, OrderAttachmentGetError, TData>, 'queryKey' | 'queryFn' | 'initialData'>,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useShoppingContext(options)
  return reactQuery.useQuery<undefined, OrderAttachmentGetError, TData>({
    queryKey: queryKeyFn({
      path: '/orders/{orderId}/attachments/{attachmentId}',
      operationId: 'orderAttachmentGet',
      variables,
    }),
    queryFn: ({ signal }) => fetchOrderAttachmentGet({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type OrderCancelPathParams = {
  /**
   * ID of the order to cancel
   */
  orderId: string
}

export type OrderCancelError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
  | {
      status: 404
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
  | {
      status: 409
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
>

export type OrderCancelVariables = {
  pathParams: OrderCancelPathParams
} & ShoppingContext['fetcherOptions']

/**
 * request to cancel an order
 */
export const fetchOrderCancel = (variables: OrderCancelVariables, signal?: AbortSignal) =>
  shoppingFetch<undefined, OrderCancelError, undefined, {}, {}, OrderCancelPathParams>({
    url: '/orders/{orderId}/cancel',
    method: 'post',
    ...variables,
    signal,
  })

/**
 * request to cancel an order
 */
export const useOrderCancel = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, OrderCancelError, OrderCancelVariables>, 'mutationFn'>,
) => {
  const { fetcherOptions } = useShoppingContext()
  return reactQuery.useMutation<undefined, OrderCancelError, OrderCancelVariables>({
    mutationFn: (variables: OrderCancelVariables) => fetchOrderCancel({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type CreditGetError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
  | {
      status: 404
      payload: {
        /**
         * Error type
         */
        type: string
        /**
         * HTTP status
         */
        status: number
        /**
         * Error message
         */
        title: string
        /**
         * Unique return code
         *
         * @format int32
         */
        rc?: number
      }
    }
>

export type CreditGetVariables = ShoppingContext['fetcherOptions']

/**
 * retrieve credit information
 */
export const fetchCreditGet = (variables: CreditGetVariables, signal?: AbortSignal) =>
  shoppingFetch<Schemas.CreditGetResult, CreditGetError, undefined, {}, {}, {}>({
    url: '/credit',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * retrieve credit information
 */
export const useCreditGet = <TData = Schemas.CreditGetResult>(
  variables: CreditGetVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.CreditGetResult, CreditGetError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useShoppingContext(options)
  return reactQuery.useQuery<Schemas.CreditGetResult, CreditGetError, TData>({
    queryKey: queryKeyFn({
      path: '/credit',
      operationId: 'creditGet',
      variables,
    }),
    queryFn: ({ signal }) => fetchCreditGet({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type CreditNoteGetError = Fetcher.ErrorWrapper<{
  status: 400
  payload: {
    /**
     * Error type
     */
    type: string
    /**
     * HTTP status
     */
    status: number
    /**
     * Error message
     */
    title: string
    /**
     * Unique return code
     *
     * @format int32
     */
    rc?: number
  }
}>

export type CreditNoteGetVariables = ShoppingContext['fetcherOptions']

/**
 * retrieve credit note information
 */
export const fetchCreditNoteGet = (variables: CreditNoteGetVariables, signal?: AbortSignal) =>
  shoppingFetch<Schemas.CreditNoteResult, CreditNoteGetError, undefined, {}, {}, {}>({
    url: '/credit/note',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * retrieve credit note information
 */
export const useCreditNoteGet = <TData = Schemas.CreditNoteResult>(
  variables: CreditNoteGetVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.CreditNoteResult, CreditNoteGetError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useShoppingContext(options)
  return reactQuery.useQuery<Schemas.CreditNoteResult, CreditNoteGetError, TData>({
    queryKey: queryKeyFn({
      path: '/credit/note',
      operationId: 'creditNoteGet',
      variables,
    }),
    queryFn: ({ signal }) => fetchCreditNoteGet({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type QueryOperation =
  | {
      path: '/orders'
      operationId: 'orderList'
      variables: OrderListVariables
    }
  | {
      path: '/orders/{orderId}'
      operationId: 'orderGet'
      variables: OrderGetVariables
    }
  | {
      path: '/orders/{orderId}/attachments/{attachmentId}'
      operationId: 'orderAttachmentGet'
      variables: OrderAttachmentGetVariables
    }
  | {
      path: '/credit'
      operationId: 'creditGet'
      variables: CreditGetVariables
    }
  | {
      path: '/credit/note'
      operationId: 'creditNoteGet'
      variables: CreditNoteGetVariables
    }
